import { useCallback, useEffect, useState } from 'react';
import { IconProviderValue } from './editor-side-bar/SideBarTabIcon';
import { TemplateEditorTabs } from '../constants';
import { useTranslation } from 'react-i18next';
import { TooltipButton } from './common/TooltipButton';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { PanelIconSection, SectionBreak, TabsBar } from '../components/style';
import { TemplateEditorImportSnapshotModal } from './modals/TemplateEditorImportSnapshotModal';
import { useParams, useLocation } from 'react-router-dom';
import { seedRequiredCompilationContextFields } from '../utils/compilation-context-helpers';
import { useFeatureFlags } from 'core/app/AppContext';
import { TemplateEditorOnsiteValidationModal } from './modals/TemplateEditorOnsiteValidationModal';
import { useGetRandomSnapshot } from '../hooks/useGetRandomSnapshot';
import { useConvertTemplate } from '../hooks/useConvertTemplate';
import { TemplateSidebarTab } from './TemplateSidebarTab';

export const TemplateEditorSideTabBar = () => {
  const { t } = useTranslation(['designs', 'trackingLinks']);
  const { flagEnabled } = useFeatureFlags();
  useParams<{ siteId: string }>();

  const { randomCompilationContexts, onsiteValidationMessage } = useTemplateEditorState();
  const dispatch = useTemplateEditorDispatch();

  const [snapshotModalOpen, setSnapshotModelOpen] = useState(false);
  const [validationMessageModalOpen, setValidationMessageModalOpen] = useState(false);

  const tplHistoryEnabled = flagEnabled('ld-template-version-history');

  const { pathname } = useLocation();
  const onsiteTemplate = pathname.includes('onsite');

  const { refetch } = useGetRandomSnapshot();

  // Set a compilation context to the first index when we load them in, seeding in required fields for rendering
  useEffect(() => {
    if (randomCompilationContexts && randomCompilationContexts !== null && randomCompilationContexts.length > 0) {
      const adjustedCompilationContext = seedRequiredCompilationContextFields(randomCompilationContexts[0]);
      dispatch({
        type: 'SET_RANDOM_COMPILATION_CONTEXT',
        payload: {
          compilationContext: adjustedCompilationContext,
          index: 0,
        },
      });
    }
  }, [randomCompilationContexts, dispatch]);

  const onInsertCompilationContextIconClick = useCallback(() => setSnapshotModelOpen(true), []);

  const onRandomSnapshotClick = useCallback(() => refetch(), [refetch]);

  const onConvertTemplate = useConvertTemplate();

  return (
    <>
      <TabsBar>
        <PanelIconSection>
          <TemplateSidebarTab
            icon="code"
            tabName={TemplateEditorTabs.templateInput}
            tooltipHeading={t('designs:designGallery.templateEditor.tabBar.input')}
            tooltipDescription={t('designs:designGallery.templateEditor.tabBar.inputDescription')}
            dataTestId="CodeIcon"
          />
          <TemplateSidebarTab
            icon="heart-rate"
            tabName={TemplateEditorTabs.compilationContextInput}
            tooltipHeading={t('designs:designGallery.templateEditor.tabBar.compilationContext')}
            tooltipDescription={t('designs:designGallery.templateEditor.tabBar.compilationContextDescription')}
            dataTestId="CompilationContextIcon"
          />
          <TemplateSidebarTab
            icon="link"
            tabName={TemplateEditorTabs.linkConfig}
            tooltipHeading={t('designs:designGallery.templateEditor.tabBar.trackingSettings')}
            dataTestId="LinkConfigIcon"
          />
        </PanelIconSection>
        <PanelIconSection>
          <SectionBreak />
          <TooltipButton
            tooltipHeading={t('trackingLinks:importSessionModal.heading')}
            icon="cloud-upload"
            onClick={onInsertCompilationContextIconClick}
          />
          <TooltipButton
            tooltipHeading={t('trackingLinks:sessionRandomiser.heading')}
            icon="exchange"
            onClick={onRandomSnapshotClick}
          />

          <TooltipButton
            tooltipHeading={t('designs:designGallery.templateEditor.tabBar.convertTemplateButton')}
            tooltipDescription={t('designs:designGallery.templateEditor.tabBar.convertTemplateDescription')}
            icon="arrow-right"
            onClick={onConvertTemplate}
          />

          {randomCompilationContexts && randomCompilationContexts.length > 0 && (
            <TemplateSidebarTab
              icon="list-alt"
              tabName={TemplateEditorTabs.compilationContextListInput}
              tooltipHeading={t('designs:designGallery.templateEditor.tabBar.randomisedList.heading')}
              tooltipDescription={t('designs:designGallery.templateEditor.tabBar.randomisedList.description')}
              dataTestId="CCListIcon"
            />
          )}
          {tplHistoryEnabled && (
            <TemplateSidebarTab
              icon="clock-rotate-left"
              iconProvider={IconProviderValue.UiComponentLibrary}
              tabName={TemplateEditorTabs.templateHistory}
              tooltipHeading={t('designs:designGallery.templateEditor.tabBar.templateHistory')}
              dataTestId="ClockIcon"
            />
          )}
          {onsiteTemplate && (
            <>
              <PanelIconSection>
                <TemplateSidebarTab
                  active={validationMessageModalOpen}
                  icon="font"
                  onClick={() => setValidationMessageModalOpen(true)}
                  tooltipHeading="Validation Message"
                  tooltipDescription="configure the validation message for this template."
                  dataTestId="validation-message"
                />
              </PanelIconSection>
              <TemplateEditorOnsiteValidationModal
                validationMessage={onsiteValidationMessage}
                onsiteValidationMessageModalOpen={validationMessageModalOpen}
                setOnsiteValidationMessageModalOpen={setValidationMessageModalOpen}
              />
            </>
          )}
          <TemplateSidebarTab
            icon="clock-rotate-left"
            iconProvider={IconProviderValue.UiComponentLibrary}
            tabName={TemplateEditorTabs.templateHistory}
            tooltipHeading={t('designs:designGallery.templateEditor.tabBar.templateHistory')}
            dataTestId="ClockIcon"
            aria-label={t('designs:designGallery.templateEditor.tabBar.templateHistory')}
          />
        </PanelIconSection>
      </TabsBar>
      <TemplateEditorImportSnapshotModal
        snapshotModalOpen={snapshotModalOpen}
        setSnapshotModalOpen={setSnapshotModelOpen}
        isOnsite={onsiteTemplate}
      />
    </>
  );
};
